import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Collapse, useMediaQuery, useTheme } from '@mui/material'
import { useFormikContext } from 'formik'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SettingsBox } from 'src/Elements/Data'
import { makeStyles } from 'tss-react/mui'
import { DppBatteryStatus, DynamicPricesSettings } from '../../types'
import { getBatterySettingsFields } from '../../utils/form-fields'
import DppBatteryToggle from './DppBatteryToggle'
import { useScrollToHash } from 'src/Libs/navigation/hooks'
import { SystemSettingHashes } from 'src/Libs/navigation/types'
import { useIsDynamicEmsAccessible } from '../../hooks'
import { FORM_DEPTH } from './battery-form.constants'

const useStyles = makeStyles()(theme => ({
  accordion: {
    /** for the content only */
    '.MuiAccordionDetails-root': {
      padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
    },
    '&.Mui-expanded , :not(&.Mui-expanded)': {
      marginBottom: theme.spacing(2),
      marginTop: 0,
    },
  },
}))

interface Props {
  hasWritingRights?: boolean
}

const SCROLL_OFFSET = 100

/**
 * This component renders the dynamic prices form fields related to the battery.
 */
const BatteryFormContent: React.FC<Props> = ({ hasWritingRights = false }) => {
  const { t } = useCustomTranslation()
  const { isDynamicEmsAccessible, isDynamicEmsV2Accessible } = useIsDynamicEmsAccessible()
  const { values, setFieldValue } = useFormikContext<DynamicPricesSettings>()
  const [dppBatteryStatus, setDppBatteryStatus] = useState<DppBatteryStatus>(getDppBatteryStatusValue(values))
  const { classes } = useStyles()
  const accordionRef = useRef<HTMLDivElement | null>(null)
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'))
  const offset = isLgUp ? SCROLL_OFFSET : 0

  useEffect(() => {
    setDppBatteryStatus(getDppBatteryStatusValue(values))
  }, [values])

  useScrollToHash(accordionRef, SystemSettingHashes.DynamicPowerPriceSettingsBattery, offset)

  const selectedMonthList = useMemo(() => values?.activeMonthsBattery || [], [values?.activeMonthsBattery])
  const isAccordionExpanded = isDynamicEmsAccessible
    ? dppBatteryStatus === 'enabledWithDynamicEms' || dppBatteryStatus === 'enabled'
    : values.dynamicPricesForBatteryEnabled

  const handleUpdateSelectedMonthList = useCallback(
    (updatedSelectedMonthList: number[]): void => {
      setFieldValue('activeMonthsBattery', updatedSelectedMonthList)
    },
    [setFieldValue]
  )

  const batterySettingsFields = useMemo(
    () =>
      getBatterySettingsFields(
        hasWritingRights,
        isDynamicEmsAccessible,
        isDynamicEmsV2Accessible,
        dppBatteryStatus,
        selectedMonthList,
        handleUpdateSelectedMonthList,
        t
      ),
    [hasWritingRights, isDynamicEmsAccessible, isDynamicEmsV2Accessible, dppBatteryStatus, selectedMonthList, handleUpdateSelectedMonthList, t]
  )

  const accordionTitle = t('systemSettings.price-based-charging.battery.section-title')

  return (
    <AccordionItem ref={accordionRef} className={classes.accordion} title={accordionTitle} depth={FORM_DEPTH} elevation={0} expanded={true}>
      <DppBatteryToggle hasWritingRights={hasWritingRights} dppBatteryStatus={dppBatteryStatus} setDppBatteryStatus={setDppBatteryStatus} />

      <Collapse in={isAccordionExpanded}>
        {batterySettingsFields.map(field => (
          <SettingsBox
            key={field.key}
            title={t(field.titleKey || '')}
            suffixSx={
              field.key === 'ems-based-price-limit-battery'
                ? { flexDirection: 'row', justifyContent: downSm ? 'flex-start' : 'end', alignItems: 'center' }
                : undefined
            }
            titleIcon={field?.titleIcon}
            description={t(field.descriptionKey)}
            suffix={field.suffix}
            depth={FORM_DEPTH}
            disabled={!hasWritingRights}
          />
        ))}
      </Collapse>
    </AccordionItem>
  )
}

export default BatteryFormContent

const getDppBatteryStatusValue = (formValues: DynamicPricesSettings): DppBatteryStatus => {
  if (formValues.isDynamicEmsEnabledForBattery) {
    return 'enabledWithDynamicEms'
  }
  if (formValues.isDynamicEmsEnabledForBattery) {
    return 'enabledWithDynamicEms'
  }
  if (!formValues.isDynamicEmsEnabledForBattery && formValues.dynamicPricesForBatteryEnabled) {
    return 'enabled'
  }
  return 'disabled'
}
